import { useNavigate } from "react-router-dom";
import { useTranslation } from "@/hooks/useTranslation";
import { getLoginPath, getHomePath, getPricingPath, getSettingsPath } from "@/utils/localePaths";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import { User, Settings, LogOut, ArrowLeft } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import Footer from "@/components/Footer";

function KVKKPage() {
  const navigate = useNavigate();
  const { locale } = useTranslation();
  const { user, logout } = useAuth();

  const getPlanDisplayName = (planType) => {
    const planNames = {
      'free': 'Free',
      'pro_monthly': 'Pro Monthly',
      'business_monthly': 'Business Monthly',
      'persona_staff': 'Staff'
    };
    return planNames[planType] || planType;
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-gradient-to-b from-black via-gray-900 to-black pt-2 pb-1 px-4">
        <div className="mb-1 px-4">
          <div className="flex justify-between items-center gap-4 bg-gray-900/50 backdrop-blur-sm rounded-lg p-2 border border-gray-800">
            <div 
              className="cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => navigate(getHomePath(locale))}
            >
              <img 
                src="/opara-logo-trimmed.png" 
                alt="OPARA Logo" 
                className="h-10 w-auto object-contain"
              />
            </div>
            
            {user && (
              <div className="flex items-center gap-2">
                <div className="text-sm bg-gray-800 px-3 py-1 rounded-lg border border-gray-700">
                  <span className="text-white">Credits: </span>
                  <span className="text-white font-bold">
                    {user?.plan_type === "persona_staff" ? "∞" : user?.credits_remaining}
                  </span>
                </div>
                
                <Link to={getPricingPath(locale)}>
                  <Button className="bg-[#FFDD59] hover:bg-[#E6C84D] text-black font-bold py-1 text-sm">
                    Upgrade
                  </Button>
                </Link>
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FFDD59] to-[#E6C84D] flex items-center justify-center hover:opacity-90 transition-opacity cursor-pointer border-2 border-gray-700 hover:border-[#FFDD59]">
                      <User className="w-4 h-4 text-black" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56 bg-gray-900 border-gray-700" align="end">
                    <DropdownMenuLabel className="text-white">
                      <div className="flex flex-col space-y-1">
                        <p className="text-sm font-medium">
                          {user?.first_name} {user?.last_name}
                        </p>
                        <p className="text-xs text-gray-400">
                          Plan: <span className="text-[#FFDD59]">{getPlanDisplayName(user?.plan_type)}</span>
                        </p>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator className="bg-gray-700" />
                    <DropdownMenuItem asChild className="cursor-pointer text-gray-300 hover:text-white hover:bg-gray-800">
                      <Link to={getSettingsPath(locale)} className="flex items-center">
                        <Settings className="w-4 h-4 mr-2" />
                        Account Settings
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-gray-700" />
                    <DropdownMenuItem onClick={logout} className="cursor-pointer text-red-400 hover:text-red-300 hover:bg-gray-800">
                      <LogOut className="w-4 h-4 mr-2" />
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}

            {!user && (
              <div className="flex items-center gap-2">
                <Link to={getPricingPath(locale)}>
                  <Button className="bg-[#FFDD59] hover:bg-[#FFE875] text-black font-bold w-40 py-1 text-sm">
                    Pricing
                  </Button>
                </Link>
                <Link to={getLoginPath(locale)}>
                  <Button className="bg-orange-500 hover:bg-orange-600 text-white font-bold w-40 py-1 text-sm">
                    SIGN IN
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="relative z-10 px-4 py-4">
        <div className="max-w-[720px] mx-auto">

          {/* Hero - Back Button + Logo */}
          <div className="flex items-start gap-4 mb-6">
            <button 
              onClick={() => navigate(-1)}
              className="flex items-center gap-1.5 text-gray-500 hover:text-[#FFDD59] transition-colors text-sm mt-1"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back</span>
            </button>
            
            <div className="flex-1 text-center">
              <img 
                src="/opara-logo-trimmed.png" 
                alt="OPARA" 
                className="h-10 w-auto mx-auto"
              />
            </div>
            
            <div className="w-[52px]"></div>
          </div>

          {/* Content Sections */}
          <div className="space-y-5">
            
            {/* Title Section */}
            <section className="border-l-2 border-[#FFDD59] pl-4 mb-6">
              <h1 className="text-base font-semibold text-[#FFDD59] mb-2">
                Privacy Policy
              </h1>
              <p className="text-sm text-gray-500">
                Effective Date: 21 July 2026
              </p>
            </section>

            {/* Intro */}
            <section className="bg-gray-900/30 rounded-lg px-4 py-3 border border-gray-800/40">
              <p className="text-sm text-gray-400 leading-relaxed">
                OPARA ("we," "us," or "our"), operated by <span className="text-gray-300">Persona Event And Marketing Solutions FZ LLE</span>, is a UAE-based B2B platform providing influencer marketing analytics and ROI projections. We respect your privacy and are committed to protecting your Personal Data. This Policy describes how we collect, use, and share your Personal Data when you interact with our Platform (opara.co).
              </p>
            </section>

            {/* Section 1 */}
            <section className="bg-gray-900/30 rounded-lg px-4 py-3 border border-gray-800/40">
              <h2 className="text-sm font-semibold text-white mb-3">
                1. Personal Data We Collect
              </h2>
              <p className="text-sm text-gray-400 leading-relaxed mb-3">
                We collect information that relates to an identified or identifiable natural person ("Personal Data") in the following ways:
              </p>
              
              <p className="text-sm text-gray-300 font-medium mb-2">Information you provide:</p>
              <ul className="space-y-2 text-sm mb-4">
                <li className="flex items-start gap-2">
                  <span className="text-[#FFDD59]">•</span>
                  <span className="text-gray-400"><span className="text-gray-300">Account information:</span> name, work email address, and company details when you register.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#FFDD59]">•</span>
                  <span className="text-gray-400"><span className="text-gray-300">Billing data:</span> payment information (processed securely via Stripe), including billing address and transaction history. We do not store full card numbers; card data is handled by Stripe.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#FFDD59]">•</span>
                  <span className="text-gray-400"><span className="text-gray-300">User input:</span> influencer usernames and budget data you enter for analysis.</span>
                </li>
              </ul>

              <p className="text-sm text-gray-300 font-medium mb-2">Automated information (public data):</p>
              <ul className="space-y-2 text-sm mb-4">
                <li className="flex items-start gap-2">
                  <span className="text-[#FFDD59]">•</span>
                  <span className="text-gray-400"><span className="text-gray-300">Influencer analytics:</span> we collect publicly available information about content creators and their public performance metrics (e.g., engagement, public posts) from social-media platforms to provide our ROI projections.</span>
                </li>
              </ul>

              <p className="text-sm text-gray-300 font-medium mb-2">Cookies &amp; tracking:</p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-[#FFDD59]">•</span>
                  <span className="text-gray-400">we use cookies to maintain your session, understand platform usage, and improve our Services.</span>
                </li>
              </ul>
            </section>

            {/* Section 2 */}
            <section className="bg-gray-900/30 rounded-lg px-4 py-3 border border-gray-800/40">
              <h2 className="text-sm font-semibold text-white mb-3">
                2. How We Use Personal Data
              </h2>
              <p className="text-sm text-gray-400 leading-relaxed mb-3">
                We process your data based on legitimate business interests and contractual necessity:
              </p>
              
              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="text-left py-2 pr-4 text-gray-300 font-medium">Purpose</th>
                      <th className="text-left py-2 text-gray-300 font-medium">Legal Ground</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-400">
                    <tr className="border-b border-gray-800/50">
                      <td className="py-2 pr-4">Create accounts, manage Credits, and provide ROI reports.</td>
                      <td className="py-2">Contractual necessity</td>
                    </tr>
                    <tr className="border-b border-gray-800/50">
                      <td className="py-2 pr-4">Provide mathematical projections to customers based on public data.</td>
                      <td className="py-2">Legitimate interest</td>
                    </tr>
                    <tr className="border-b border-gray-800/50">
                      <td className="py-2 pr-4">Secure the platform and prevent unauthorised or bot activity.</td>
                      <td className="py-2">Legitimate interest</td>
                    </tr>
                    <tr className="border-b border-gray-800/50">
                      <td className="py-2 pr-4">Provide technical support and send administrative updates.</td>
                      <td className="py-2">Contractual necessity</td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-4">Comply with UAE financial regulations and lawful requests.</td>
                      <td className="py-2">Legal obligation</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            {/* Section 3 - Cookie Policy */}
            <section className="bg-gray-900/30 rounded-lg px-4 py-3 border border-gray-800/40">
              <h2 className="text-sm font-semibold text-white mb-3">
                3. Cookie Policy
              </h2>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-[#FFDD59]">•</span>
                  <span className="text-gray-400"><span className="text-gray-300">Essential cookies:</span> required for system authentication, security, and account management.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#FFDD59]">•</span>
                  <span className="text-gray-400"><span className="text-gray-300">Payment &amp; fraud cookies:</span> our payment processor (Stripe) deploys cookies to prevent fraudulent transactions and ensure a secure checkout.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#FFDD59]">•</span>
                  <span className="text-gray-400"><span className="text-gray-300">Analytical cookies:</span> we may use anonymised analytics to understand platform performance.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#FFDD59]">•</span>
                  <span className="text-gray-400"><span className="text-gray-300">Your choice:</span> you can manage or disable cookies through your browser settings; however, disabling essential cookies may prevent the Platform from functioning correctly.</span>
                </li>
              </ul>
            </section>

            {/* Section 4 */}
            <section className="bg-gray-900/30 rounded-lg px-4 py-3 border border-gray-800/40">
              <h2 className="text-sm font-semibold text-white mb-3">
                4. How We Share Your Data
              </h2>
              <p className="text-sm text-gray-400 leading-relaxed mb-3">
                We do not sell your Personal Data. We share it only with:
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-[#FFDD59]">•</span>
                  <span className="text-gray-400"><span className="text-gray-300">Service providers:</span> secure third-party vendors for hosting (Google Cloud), payment processing (Stripe), and customer support, under confidentiality obligations.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#FFDD59]">•</span>
                  <span className="text-gray-400"><span className="text-gray-300">Legal requirements:</span> where required by law or to protect our legal rights, in accordance with UAE/DIFC regulations.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#FFDD59]">•</span>
                  <span className="text-gray-400"><span className="text-gray-300">Business transfers:</span> in the event of a merger, acquisition, or sale of assets.</span>
                </li>
              </ul>
            </section>

            {/* Section 5 */}
            <section className="bg-gray-900/30 rounded-lg px-4 py-3 border border-gray-800/40">
              <h2 className="text-sm font-semibold text-white mb-3">
                5. Data Retention
              </h2>
              <p className="text-sm text-gray-400 leading-relaxed mb-3">
                We retain Personal Data only for as long as necessary:
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-[#FFDD59]">•</span>
                  <span className="text-gray-400"><span className="text-gray-300">Account data:</span> retained while your account is active, plus up to three years thereafter for security and legal purposes.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#FFDD59]">•</span>
                  <span className="text-gray-400"><span className="text-gray-300">Analysis reports &amp; query data:</span> retained for approximately 90 days, after which they are automatically deleted.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#FFDD59]">•</span>
                  <span className="text-gray-400"><span className="text-gray-300">Invoicing data:</span> retained for seven years to comply with UAE tax and financial record-keeping laws.</span>
                </li>
              </ul>
            </section>

            {/* Section 6 */}
            <section className="bg-gray-900/30 rounded-lg px-4 py-3 border border-gray-800/40">
              <h2 className="text-sm font-semibold text-white mb-3">
                6. Your Rights and Choices
              </h2>
              <p className="text-sm text-gray-400 leading-relaxed">
                Depending on your jurisdiction, you may have rights to access and portability, rectification of inaccurate data, deletion (subject to legal retention requirements), and withdrawal of consent for marketing communications. To exercise any of these, contact <span className="text-[#FFDD59]">support@opara.co</span>.
              </p>
            </section>

            {/* Section 7 */}
            <section className="bg-gray-900/30 rounded-lg px-4 py-3 border border-gray-800/40">
              <h2 className="text-sm font-semibold text-white mb-2">
                7. Security & International Transfers
              </h2>
              <p className="text-sm text-gray-400 leading-relaxed">
                We implement robust technical and organisational measures to protect your data. As a global platform, your data may be transferred to and processed in countries outside your residence. We ensure such transfers are made using secure mechanisms consistent with applicable data-protection laws.
              </p>
            </section>

            {/* Section 8 */}
            <section className="bg-gray-900/30 rounded-lg px-4 py-3 border border-gray-800/40">
              <h2 className="text-sm font-semibold text-white mb-2">
                8. Children's Privacy
              </h2>
              <p className="text-sm text-gray-400 leading-relaxed">
                OPARA is a B2B platform and is not directed to individuals under the age of 18. We do not knowingly collect data from children.
              </p>
            </section>

            {/* Section 9 */}
            <section className="bg-gray-900/30 rounded-lg px-4 py-3 border border-gray-800/40">
              <h2 className="text-sm font-semibold text-white mb-3">
                9. Contact Us
              </h2>
              <p className="text-sm text-gray-400 leading-relaxed mb-3">
                For any queries regarding this Policy or to exercise your rights, please contact:
              </p>
              <div className="text-sm text-gray-400 space-y-1">
                <p className="text-gray-300 font-medium">Persona Event And Marketing Solutions FZ LLE</p>
                <p>Email: <a href="mailto:support@opara.co" className="text-[#FFDD59] hover:underline">support@opara.co</a></p>
                <p>Address: Twin Towers, M1 Floor, Creative City, Fujairah Media Free Zone, Fujairah, UAE</p>
              </div>
            </section>

          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
}

export default KVKKPage;
